/**
 * FolderBrowser (2026-08-20)
 *
 * The ONE folder-drill-down browser for storage scoping, extracted from
 * FolderScopePicker so the onboarding scope sheet and the Integrations picker
 * share a single implementation (owner report 2026-08-20: onboarding offered
 * only a flat top-level list — no way to choose a subfolder).
 *
 * Behavior: breadcrumb trail with per-level listings cached for the session,
 * checkbox per folder (a chosen folder is a master folder — its whole subtree
 * is included; enforcement is subtree-based server-side, storageScope BFS),
 * "Open ›" drills in and subfolders are choosable at ANY depth, a text filter
 * for the current level, root-level "Shared with you" section, error rows
 * with a live retry, and selection chips that span levels so drill-down picks
 * stay visible. Selection state is CONTROLLED by the parent: `onToggle`
 * receives the folder enriched with a human path built from the crumb trail,
 * ready to persist via setStorageScope.
 */

import React, { useState, useEffect } from 'react';
import { browseStorageFolders, type StorageScopeFolder } from './ComposioClient';

interface Crumb {
  id: string;
  name: string;
}

export interface FolderBrowserProps {
  /** Toolkit slug of the connected storage app (e.g. 'googledrive'). */
  appSlug: string;
  /** Human-readable app name — labels the root crumb. */
  appDisplayName: string;
  /** Multi-account: browse THIS connection instead of the primary. */
  connectionId?: string;
  /** Current selection, keyed by folder id. */
  selected: Record<string, StorageScopeFolder>;
  /** Toggle a folder in/out of the selection (enriched with `path`). */
  onToggle: (folder: StorageScopeFolder) => void;
  /**
   * Optional pre-fetched ROOT listing. The onboarding sheet loads root with
   * its own retry belt before mounting the browser; seeding avoids a second
   * fetch of the same level.
   */
  initialRootListing?: StorageScopeFolder[];
}

/** Map an API error to user copy (server 403 strings pass through). */
function describeBrowseError(err: any): string {
  const msg = String(err?.message || '').trim();
  if (!msg || /^HTTP \d+$/i.test(msg)) return 'Something went wrong. Please try again.';
  return msg;
}

export const FolderBrowser: React.FC<FolderBrowserProps> = ({
  appSlug,
  appDisplayName,
  connectionId,
  selected,
  onToggle,
  initialRootListing,
}) => {
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: 'root', name: appDisplayName }]);
  const [listings, setListings] = useState<Record<string, StorageScopeFolder[]>>(
    (): Record<string, StorageScopeFolder[]> => (initialRootListing ? { root: initialRootListing } : {}));
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const [filter, setFilter] = useState('');

  const currentFolder = crumbs[crumbs.length - 1];
  const currentListing = listings[currentFolder.id];

  // Fetch the current level's child folders (cache-aware; retryTick re-arms after errors)
  useEffect(() => {
    if (listings[currentFolder.id]) {
      // Arriving at a CACHED level must clear transient fetch state (review
      // 2026-08-20): breadcrumbing back mid-fetch cancels the in-flight
      // request (its .finally is skipped), and a deep-level error would
      // otherwise shadow the cached listing with a no-op "Try again" —
      // either way the browser wedged on Loading/error forever.
      setBrowseLoading(false);
      setBrowseError(null);
      return;
    }
    let cancelled = false;
    setBrowseLoading(true);
    setBrowseError(null);
    browseStorageFolders(appSlug, currentFolder.id, connectionId)
      .then((folders) => {
        if (!cancelled) {
          setListings((prev) => ({ ...prev, [currentFolder.id]: folders }));
        }
      })
      .catch((err: any) => {
        if (!cancelled) setBrowseError(describeBrowseError(err));
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appSlug, currentFolder.id, listings, retryTick, connectionId]);

  const selectedList = Object.keys(selected).map((id) => selected[id]);

  const toggleFolder = (folder: StorageScopeFolder) => {
    // Build a friendly path from the breadcrumb trail (crumb 0 is the app root)
    const parentPath = crumbs.slice(1).map((c) => c.name);
    onToggle({
      id: folder.id,
      name: folder.name,
      ...(folder.shared ? { shared: true } : {}),
      path:
        folder.path ||
        (folder.shared
          ? ['Shared with me', ...parentPath, folder.name].join(' / ')
          : [...parentPath, folder.name].join(' / ')),
    });
  };

  const enterFolder = (folder: StorageScopeFolder) => {
    setCrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setFilter('');
  };

  const goToCrumb = (index: number) => {
    setCrumbs((prev) => prev.slice(0, index + 1));
    setFilter('');
  };

  const filterText = filter.trim().toLowerCase();
  const visibleFolders = (currentListing || []).filter(
    (f) => !filterText || f.name.toLowerCase().includes(filterText)
  );
  // Root level lists My Drive folders AND folders shared to this account
  // (owner case 2026-08-11: the folder someone wants is often shared, not
  // owned). Rendered as one list with a labeled "shared with you" section —
  // rows behave identically (check, open, drill down).
  const ownedFolders = visibleFolders.filter((f) => !f.shared);
  const sharedFolders = visibleFolders.filter((f) => f.shared);

  const renderFolderRow = (f: StorageScopeFolder) => (
    <div
      key={f.id}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.45rem 0.6rem', borderBottom: '1px solid #F3F4F6',
        background: selected[f.id] ? 'rgba(198, 167, 94, 0.12)' : '#fff',
      }}
    >
      <input
        type="checkbox"
        checked={!!selected[f.id]}
        onChange={() => toggleFolder(f)}
        aria-label={`Use ${f.name}`}
        style={{ cursor: 'pointer', accentColor: 'var(--brand-gold)' }}
      />
      <span aria-hidden="true">{f.shared ? '🤝' : '📁'}</span>
      <span
        onClick={() => toggleFolder(f)}
        style={{ flex: 1, fontSize: '0.85rem', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {f.name}
      </span>
      <button
        type="button"
        onClick={() => enterFolder(f)}
        title={`Open ${f.name} — choose from its subfolders`}
        style={{
          background: 'none', border: '1px solid #E5E7EB', borderRadius: '6px',
          cursor: 'pointer', color: '#6B7280', fontSize: '0.75rem', padding: '0.1rem 0.45rem',
          flexShrink: 0,
        }}
      >
        Open ›
      </button>
    </div>
  );

  return (
    <div>
      {/* Breadcrumb trail */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.5rem', fontSize: '0.78rem' }}>
        {crumbs.map((crumb, i) => (
          <React.Fragment key={crumb.id}>
            {i > 0 && <span style={{ color: '#9CA3AF' }}>›</span>}
            {i < crumbs.length - 1 ? (
              <button
                type="button"
                onClick={() => goToCrumb(i)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-gold-hover)', fontSize: '0.78rem', fontFamily: 'inherit' }}
              >
                {crumb.name}
              </button>
            ) : (
              <span style={{ color: '#4B5563', fontWeight: 600 }}>{crumb.name}</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Text filter for the current level */}
      <input
        type="text"
        placeholder="Filter folders..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.75rem',
          borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '0.85rem',
          outline: 'none', marginBottom: '0.5rem', fontFamily: 'inherit',
        }}
      />

      {/* Folder list */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', marginBottom: '0.6rem', background: '#fff' }}>
        {browseLoading ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>
            Loading folders...
          </div>
        ) : browseError ? (
          <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem' }}>
            <div style={{ color: '#DC2626', marginBottom: '0.5rem' }}>{browseError}</div>
            <button
              type="button"
              onClick={() => setRetryTick((t) => t + 1)}
              style={{
                padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem',
                border: 'none', background: 'var(--brand-gold)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        ) : visibleFolders.length === 0 ? (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' }}>
            {filterText
              ? `No folders matching "${filter.trim()}"`
              : crumbs.length > 1
                ? 'No subfolders here'
                : 'No folders found in this account'}
          </div>
        ) : (
          <>
            {ownedFolders.map(renderFolderRow)}
            {sharedFolders.length > 0 && (
              <div style={{
                padding: '0.45rem 0.6rem 0.3rem',
                fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
                textTransform: 'uppercase', color: '#9CA3AF',
                borderBottom: '1px solid #F3F4F6',
                background: '#FAFAF9',
              }}>
                Shared with you
              </div>
            )}
            {sharedFolders.map(renderFolderRow)}
          </>
        )}
      </div>

      {/* Selection summary — spans levels, so drill-down picks stay visible */}
      {selectedList.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: '#6B7280', fontWeight: 500 }}>
            Prest0n will use:
          </span>
          {selectedList.map((f) => (
            <span
              key={f.id}
              title={f.path || f.name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                fontSize: '0.75rem', color: '#7a5f1f', background: 'rgba(198, 167, 94, 0.14)',
                padding: '0.15rem 0.5rem', borderRadius: '12px', fontWeight: 500,
              }}
            >
              📁 {f.name}
              <button
                type="button"
                onClick={() => onToggle(f)}
                title={`Remove ${f.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a5f1f', fontSize: '0.8rem', padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#9CA3AF' }}>
          Choose at least one folder — open any folder to pick from its subfolders.
        </div>
      )}
    </div>
  );
};

export default FolderBrowser;
